import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
    Plus, Search, Pencil, Ban, RotateCcw, X, Loader2, AlertTriangle, Clock,
    Droplet, Radiation, ScanLine, ShieldAlert, Baby, Info, Library,
} from 'lucide-react';
import PickFromMasterModal from '../../components/catalog/PickFromMasterModal';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Pagination from '../../components/Pagination';
import CommonLoader from '../../components/CommonLoader';
// A factory, not a hook — imported under its real name so
// react-hooks/rules-of-hooks does not key on the identifier. See usePermissions.js.
import createAuthedAxios from '../../hooks/useAxiosAuth';
import usePermissions from '../../hooks/usePermissions';

const MySwal = withReactContent(Swal);

// THE SCAN MENU — every examination this department is willing to perform, on
// what class of machine, for how long, at what price, and what the patient has to
// do before they arrive.
//
// A scan ORDER is a ServiceOrder with orderType 'radiology'; this screen is only
// the menu those orders are written against. procedureId is what a study, a
// report and a service_order_item line all point back at, which is why a
// procedure is WITHDRAWN and never deleted — a report reopened in ten years has
// to be able to say what examination it was.
//
// ---------------------------------------------------------------------------
// WHY THE PC-PNDT BADGE IS THE LOUDEST THING ON THE SCREEN
//
// The Pre-Conception and Pre-Natal Diagnostic Techniques Act governs any scan
// capable of revealing the sex of a foetus. Breaching it is a CRIMINAL offence —
// imprisonment and erasure from the medical register — not a line on a penalty
// notice. Everything else on this page (a price, a duration, a preparation note)
// is at worst an inconvenience if it is wrong; this one column decides whether an
// obstetric ultrasound is performed inside the register the Act requires or
// outside it.
//
// So it is coded FOUR ways at once, on purpose: a red rail down the left of the
// row, a filled red badge, the words 'PC-PNDT · FORM F', and a tinted row. Once,
// in colour alone, is not enough — roughly one man in twelve cannot separate the
// red from the amber, and the person auditing this menu is frequently reading it
// on a printout. The same reasoning as the STAT rail on the lab worklist, for a
// consequence that is considerably worse.
//
// AND WHY IT IS A TICK-BOX AND NOT DERIVED FROM THE NAME. An obstetric scan, a
// level II anomaly scan, an NT scan, a follicular study and a plain pelvic
// ultrasound on a woman of reproductive age are all in scope, share no common
// substring, and are named differently in every hospital that types its own menu.
// A name-matching rule would let exactly one locally-worded procedure — the one
// nobody thought to test — perform foetal ultrasound outside the Act's register.
// Ticking it wrongly ON costs a form. Leaving it wrongly OFF costs the licence.
// That asymmetry is why the form below argues the case rather than presenting a
// bare checkbox, and why the list carries a standing count of what is in scope.
//
// ---------------------------------------------------------------------------
// THE OTHER THING THIS SCREEN IS FOR: THE GAPS NOBODY SEES UNTIL A PATIENT IS
// STANDING THERE.
//
//  · A CONTRAST STUDY WITH NO PREPARATION TEXT. requiresContrast drives the
//    consent, the renal-function check and the fasting instruction. If the
//    sentence the patient must follow was never written, they arrive having
//    eaten, and a scanner hour is lost — or worse, the cannula goes in without
//    anybody having asked about a previous reaction.
//  · AN ULTRASOUND WITH NO PREPARATION TEXT. 'Full bladder, do not void' is the
//    single most common reason an abdominal or pelvic scan is abandoned and
//    rebooked.
//  · A MODALITY WITH NO DURATION SET SENSIBLY. durationMins is how long the study
//    HOLDS THE MACHINE, and the scheduler blocks exactly that. Left at the
//    fifteen-minute default an MRI brain stacks three patients into one scanner
//    hour, which is two people who travelled and are sent home.
// None of the three breaks anything the software can detect, so they are counted
// on the page in front of the reader rather than left to be discovered.
//
// ---------------------------------------------------------------------------
// NO REDUX SLICE, deliberately — the same position the pharmacy master screens
// take (see the note at the top of RackLocations.jsx). Registering one means
// touching src/store.js, which this screen ships ahead of. Every call goes
// through the one axios instance built below, so there is exactly one spelling of
// each endpoint in this file.
//
// PERMISSIONS ARE NOT ENFORCED HERE. requireTenantFeature('radiology') and
// requirePermission('radiology.master.manage') guard every route, and the model
// layer scopes each read to one hospital. The can() check below only decides
// whether a control is drawn and, when it is not, SAYS SO IN WORDS — somebody who
// cannot edit the menu needs to learn that from the screen, not from a 403 after
// they have filled in a form.

const API = {
    list: '/radiology/procedures',
    one: (procedureId) => `/radiology/procedures/${procedureId}`,
};

const apiError = (err, fallback) =>
    err?.response?.data?.message || err?.message || fallback;

const money = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0).toFixed(2);

/** Duration said the way a person says it. 45 is '45 min'; 90 is '1 h 30 min'. */
const formatDuration = (mins) => {
    const m = parseInt(mins, 10);
    if (!Number.isFinite(m) || m <= 0) return '—';
    if (m < 60) return `${m} min`;
    const h = Math.floor(m / 60);
    const rest = m % 60;
    return rest ? `${h} h ${rest} min` : `${h} h`;
};

// Mirrors the `modality` ENUM on Backend/models/imagingProcedureModel.js — and,
// identically, the one on imagingEquipmentModel.js. The two lists MUST stay the
// same: scheduling matches procedure.modality against equipment.modality, so a
// value that exists on one side and not the other is a procedure no machine can
// ever be found for, and the scheduler answers "no equipment available" for a
// room standing empty.
//
// `ionising` is the AERB-registered set (xray, ct, mammography, dexa). It is
// carried here only to explain the radiation-dose field to whoever is filling the
// form in; the enforcement lives in services/radiology/imagingService.js, which
// is the one place allowed to decide whether a machine may be used today.
const MODALITIES = {
    xray: { labelKey: 'imagingprocedures.modality_xray', label: 'X-ray', chip: 'bg-slate-100 text-slate-700 border-slate-300', ionising: true },
    ultrasound: { labelKey: 'imagingprocedures.modality_ultrasound', label: 'Ultrasound', chip: 'bg-sky-50 text-sky-800 border-sky-300', ionising: false },
    ct: { labelKey: 'imagingprocedures.modality_ct', label: 'CT', chip: 'bg-amber-50 text-amber-800 border-amber-300', ionising: true },
    mri: { labelKey: 'imagingprocedures.modality_mri', label: 'MRI', chip: 'bg-violet-50 text-violet-800 border-violet-300', ionising: false },
    mammography: { labelKey: 'imagingprocedures.modality_mammography', label: 'Mammography', chip: 'bg-rose-50 text-rose-800 border-rose-300', ionising: true },
    dexa: { labelKey: 'imagingprocedures.modality_dexa', label: 'DEXA', chip: 'bg-orange-50 text-orange-800 border-orange-300', ionising: true },
    echo: { labelKey: 'imagingprocedures.modality_echo', label: 'Echo', chip: 'bg-emerald-50 text-emerald-800 border-emerald-300', ionising: false },
    ecg: { labelKey: 'imagingprocedures.modality_ecg', label: 'ECG', chip: 'bg-teal-50 text-teal-800 border-teal-300', ionising: false },
    other: { labelKey: 'imagingprocedures.modality_other', label: 'Other', chip: 'bg-gray-100 text-gray-600 border-gray-300', ionising: false },
};
const MODALITY_KEYS = Object.keys(MODALITIES);

const EMPTY_PROCEDURE = {
    procedureId: null,
    code: '',
    name: '',
    modality: 'xray',
    bodyPart: '',
    price: '',
    durationMins: 15,
    requiresContrast: false,
    isPndt: false,
    preparation: '',
    radiationDose: '',
};

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 500;

/**
 * The gaps this screen counts. See the header for why each one matters — every
 * one of them is legal configuration that the software cannot refuse, and every
 * one of them surfaces as a wasted slot or a patient turned away.
 */
const setupGaps = (p) => {
    const gaps = [];
    const prep = String(p.preparation || '').trim();
    if (p.requiresContrast && !prep) gaps.push('contrast_no_prep');
    if (p.modality === 'ultrasound' && !prep) gaps.push('ultrasound_no_prep');
    if (!Number(p.durationMins)) gaps.push('no_duration');
    return gaps;
};

/* ------------------------------------------------------------------ *
 * Presentational pieces
 *
 * Outside the component: none of them reads page state, and rebuilding them on
 * every keystroke in the search box is the one cost this screen would feel.
 * ------------------------------------------------------------------ */

/**
 * THE BADGE THIS WHOLE SCREEN EXISTS TO MAKE UNMISSABLE.
 *
 * Word, colour and a filled shape together — never colour alone. It names the
 * statute rather than saying "restricted", because a member of staff who has not
 * met the Act needs the four letters they can look up, and one who has needs no
 * explanation at all. 'FORM F' is on it because that is the operational
 * consequence: services/radiology refuses to mark the study performed without one.
 */
function PndtBadge({ compact = false, testId }) {
    const { t } = useTranslation();
    return (
        <span
            data-testid={testId}
            title={t(
                'imagingprocedures.pndt_hint',
                'Within the PC-PNDT Act. On a female patient this scan cannot be marked performed until Form F is completed, and the signed report carries the statutory declaration that the sex of the foetus has not been detected or disclosed.'
            )}
            className={`inline-flex items-center gap-1 rounded-md bg-red-600 text-white font-black uppercase tracking-widest ${compact ? 'px-1.5 py-0.5 text-[9px]' : 'px-2 py-0.5 text-[10px]'}`}
        >
            <ShieldAlert className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
            {t('imagingprocedures.pndt_badge', 'PC-PNDT · Form F')}
        </span>
    );
}

function ModalityChip({ modality }) {
    const { t } = useTranslation();
    const cfg = MODALITIES[modality] || MODALITIES.other;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-semibold ${cfg.chip}`}>
            <ScanLine className="w-3 h-3" />
            {t(cfg.labelKey, cfg.label)}
        </span>
    );
}

/** The configuration gaps, named on the row rather than discovered at the desk. */
function GapChips({ procedure }) {
    const { t } = useTranslation();
    const gaps = setupGaps(procedure);
    if (!gaps.length) return null;

    const WORDING = {
        contrast_no_prep: {
            short: t('imagingprocedures.gap_contrast_short', 'No prep note'),
            hint: t('imagingprocedures.gap_contrast_hint', 'This study uses contrast and no patient instruction is written. Fasting, the renal-function check and the allergy question all travel in that sentence.'),
        },
        ultrasound_no_prep: {
            short: t('imagingprocedures.gap_usg_short', 'No prep note'),
            hint: t('imagingprocedures.gap_usg_hint', 'Ultrasound with no instruction. "Full bladder, do not void" is the commonest reason a scan is abandoned and rebooked.'),
        },
        no_duration: {
            short: t('imagingprocedures.gap_duration_short', 'No duration'),
            hint: t('imagingprocedures.gap_duration_hint', 'The scheduler blocks this many minutes of machine time. With none set the slot is wrong for every booking made against it.'),
        },
    };

    // One chip even when two gaps coincide — a contrast ultrasound with no
    // preparation would otherwise draw the same sentence twice.
    const seen = new Set();
    return (
        <div className="flex flex-wrap gap-1">
            {gaps.map((g) => {
                const w = WORDING[g];
                if (!w || seen.has(w.short)) return null;
                seen.add(w.short);
                return (
                    <span
                        key={g}
                        data-testid={`gap-${g}-${procedure.procedureId}`}
                        title={w.hint}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-bold"
                    >
                        <AlertTriangle className="w-3 h-3" />
                        {w.short}
                    </span>
                );
            })}
        </div>
    );
}

/* ------------------------------------------------------------------ *
 * The screen
 * ------------------------------------------------------------------ */

export default function ImagingProcedures() {
    const { t } = useTranslation();
    const { can } = usePermissions();
    const axiosInstance = useMemo(() => createAuthedAxios(), []);

    const canManage = can('radiology.master.manage');

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(PAGE_SIZE);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [activeTab, setActiveTab] = useState('active');
    const [modalityFilter, setModalityFilter] = useState('');
    // '' = everything, 'true' = only what is inside the Act. There is deliberately
    // no "hide PC-PNDT" option: a filter that can conceal the regulated half of
    // the menu is a filter somebody leaves on.
    const [pndtFilter, setPndtFilter] = useState('');

    const [draft, setDraft] = useState(null);
    const [mutating, setMutating] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [showPickModal, setShowPickModal] = useState(false);

    const searchTimeoutRef = useRef(null);

    const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

    useEffect(() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearch(searchQuery.trim());
            setCurrentPage(1);
        }, SEARCH_DEBOUNCE_MS);
        return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
    }, [searchQuery]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await axiosInstance.get(API.list, {
                    params: {
                        page: currentPage,
                        limit: itemsPerPage,
                        search: debouncedSearch,
                        modality: modalityFilter,
                        isPndt: pndtFilter,
                        // A procedure is withdrawn, never deleted, so the master
                        // has to be able to show the withdrawn ones — otherwise
                        // there is no way to find the row somebody needs back.
                        isActive: activeTab === 'active' ? 'true' : 'false',
                    },
                });
                if (cancelled) return;
                setRows(res?.data?.data || []);
                setTotal(res?.data?.total || 0);
            } catch (err) {
                if (cancelled) return;
                setRows([]);
                setTotal(0);
                toast.warn(apiError(err, t('imagingprocedures.load_error', 'Could not load the scan menu')));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, itemsPerPage, debouncedSearch, activeTab, modalityFilter, pndtFilter, refreshKey]);

    // Counted on the page in front of the reader rather than across the whole
    // menu. Claiming a total the screen cannot show sends somebody hunting through
    // pages for rows that are not flagged anywhere — the position the lab test
    // master takes on its own configuration gaps, for the same reason.
    const pndtOnPage = useMemo(() => rows.filter((r) => r.isPndt).length, [rows]);
    const gapsOnPage = useMemo(() => rows.filter((r) => setupGaps(r).length > 0).length, [rows]);

    const openCreate = () => setDraft({ ...EMPTY_PROCEDURE });
    const openEdit = (p) => setDraft({
        procedureId: p.procedureId,
        code: p.code || '',
        name: p.name || '',
        modality: MODALITIES[p.modality] ? p.modality : 'other',
        bodyPart: p.bodyPart || '',
        price: p.price ?? '',
        durationMins: p.durationMins ?? 15,
        requiresContrast: !!p.requiresContrast,
        isPndt: !!p.isPndt,
        preparation: p.preparation || '',
        radiationDose: p.radiationDose || '',
    });

    const handlePageChange = (page, newItemsPerPage) => {
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
    };

    /* ---------------------------------------------------------------- *
     * Writes
     * ---------------------------------------------------------------- */

    const save = async () => {
        const code = draft.code.trim();
        const name = draft.name.trim();
        if (!code) { toast.warn(t('imagingprocedures.code_required', 'Give the procedure the code your requisitions are already written against')); return; }
        if (!name) { toast.warn(t('imagingprocedures.name_required', 'Give the procedure its reporting name')); return; }

        const durationMins = parseInt(draft.durationMins, 10);
        if (!Number.isFinite(durationMins) || durationMins <= 0) {
            // Not cosmetic: this is the machine time the scheduler blocks, so a
            // zero stacks every booking on top of the one before it.
            toast.warn(t('imagingprocedures.duration_required', 'How many minutes does this study hold the machine? The scheduler blocks exactly that.'));
            return;
        }

        const payload = {
            code,
            name,
            modality: draft.modality,
            bodyPart: draft.bodyPart.trim() || null,
            price: Number(draft.price) || 0,
            durationMins,
            requiresContrast: !!draft.requiresContrast,
            isPndt: !!draft.isPndt,
            preparation: draft.preparation.trim() || null,
            // Left null rather than defaulted to '0'. A zero printed beside a CT
            // abdomen is a false reassurance; 'not stated' is at least honest.
            radiationDose: draft.radiationDose.trim() || null,
        };

        setMutating(true);
        try {
            if (draft.procedureId) {
                await axiosInstance.put(API.one(draft.procedureId), payload);
                toast.success(t('imagingprocedures.updated', 'Procedure updated'));
            } else {
                await axiosInstance.post(API.list, payload);
                toast.success(t('imagingprocedures.created', 'Procedure added to the menu'));
            }
            setDraft(null);
            refetch();
        } catch (err) {
            // The server's refusals are the useful half and are shown verbatim —
            // a duplicate code inside this hospital, a modality no machine can
            // perform. A house string would throw away the thing to fix.
            toast.error(apiError(err, t('imagingprocedures.save_error', 'Failed to save the procedure')));
        } finally {
            setMutating(false);
        }
    };

    /**
     * Withdraw a procedure from the menu. Deactivated, never deleted: studies,
     * orders and signed reports from previous years all quote procedureId, and a
     * delete orphans a record a clinician — or an inspector — may reopen a decade
     * later.
     */
    const withdraw = async (p) => {
        const result = await MySwal.fire({
            title: t('imagingprocedures.withdraw_title', 'Withdraw this procedure?'),
            text: t('imagingprocedures.withdraw_text', 'It disappears from the order screen. Nothing is deleted — studies, orders and signed reports keep pointing at it.'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('imagingprocedures.withdraw', 'Withdraw'),
            cancelButtonText: t('imagingprocedures.cancel', 'Cancel'),
        });
        if (!result.isConfirmed) return;

        setMutating(true);
        try {
            const res = await axiosInstance.delete(API.one(p.procedureId));
            // Scans already booked against it do not block the withdrawal but
            // they are still work somebody has to finish, so the server puts the
            // count in its message rather than dropping it.
            toast.success(res?.data?.message || t('imagingprocedures.withdrawn', 'Procedure withdrawn'));
            refetch();
        } catch (err) {
            toast.warn(apiError(err, t('imagingprocedures.withdraw_error', 'Failed to withdraw the procedure')));
        } finally {
            setMutating(false);
        }
    };

    const reactivate = async (p) => {
        setMutating(true);
        try {
            await axiosInstance.put(API.one(p.procedureId), { isActive: true });
            toast.success(t('imagingprocedures.reactivated', 'Procedure is back on the menu'));
            refetch();
        } catch (err) {
            toast.warn(apiError(err, t('imagingprocedures.reactivate_error', 'Failed to bring the procedure back')));
        } finally {
            setMutating(false);
        }
    };

    const indexOfFirst = (currentPage - 1) * itemsPerPage;
    const inputCls = 'w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]';
    const labelCls = 'text-xs font-medium text-slate-500';

    /* ---- row-level renderers ---- */

    const Identity = ({ p }) => (
        <div className="flex items-start gap-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${p.isPndt ? 'bg-red-100' : 'bg-[#4ABDBD]/10'}`}>
                {p.isPndt
                    ? <Baby className="w-4 h-4 text-red-600" />
                    : <ScanLine className="w-4 h-4 text-[#2D8A8A]" />}
            </div>
            <div className="min-w-0">
                <div className="font-semibold text-gray-700 flex items-center gap-2 flex-wrap">
                    {p.name || '-'}
                    {p.isPndt && <PndtBadge testId={`pndt-badge-${p.procedureId}`} />}
                </div>
                <div className="text-[11px] text-slate-400 font-mono">
                    {p.code}{p.bodyPart ? ` · ${p.bodyPart}` : ''}
                </div>
                {p.requiresContrast && (
                    <div className="text-[11px] text-violet-700 flex items-center gap-1 mt-0.5">
                        <Droplet className="w-3 h-3" />
                        {t('imagingprocedures.contrast', 'Contrast — consent, renal check, IV line')}
                    </div>
                )}
            </div>
        </div>
    );

    const Actions = ({ p, compact = false }) => (
        <div className={`flex ${compact ? '' : 'justify-center'} gap-2`}>
            <button
                data-testid={`edit-procedure-${p.procedureId}`}
                onClick={() => openEdit(p)}
                disabled={!canManage}
                title={canManage ? t('imagingprocedures.edit', 'Edit') : t('imagingprocedures.no_permission', 'You do not have permission to change the scan menu (radiology.master.manage)')}
                className={`text-[#4ABDBD] p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 shadow-md transition-all duration-300 ${canManage ? 'hover:text-white hover:from-[#4ABDBD] hover:to-[#3AA8A8] cursor-pointer hover:scale-110' : 'opacity-40 cursor-not-allowed'}`}
            >
                <Pencil className="h-4 w-4" />
            </button>
            {p.isActive !== false ? (
                <button
                    data-testid={`withdraw-procedure-${p.procedureId}`}
                    onClick={() => withdraw(p)}
                    disabled={!canManage || mutating}
                    title={t('imagingprocedures.withdraw', 'Withdraw')}
                    className={`text-red-500 p-2 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 shadow-md transition-all duration-300 ${canManage && !mutating ? 'hover:text-white hover:from-red-500 hover:to-pink-500 cursor-pointer hover:scale-110' : 'opacity-40 cursor-not-allowed'}`}
                >
                    <Ban className="h-4 w-4" />
                </button>
            ) : (
                <button
                    data-testid={`reactivate-procedure-${p.procedureId}`}
                    onClick={() => reactivate(p)}
                    disabled={!canManage || mutating}
                    title={t('imagingprocedures.reactivate', 'Bring back')}
                    className={`text-emerald-600 p-2 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 shadow-md transition-all duration-300 ${canManage && !mutating ? 'hover:text-white hover:from-emerald-500 hover:to-teal-500 cursor-pointer hover:scale-110' : 'opacity-40 cursor-not-allowed'}`}
                >
                    <RotateCcw className="h-4 w-4" />
                </button>
            )}
        </div>
    );

    return (
        <div data-testid="imaging-procedures-page-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <div className="ml-2">
                    <h1 data-testid="imaging-procedures-page-title" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                        {t('imagingprocedures.title', 'Scan menu')}
                    </h1>
                    <p className="text-xs text-slate-400 mt-1">
                        {t('imagingprocedures.subtitle', 'What this department will scan — on which machine, for how long, at what price, and what the patient must do beforehand.')}
                    </p>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                    <button
                        data-testid="pick-from-master-button"
                        onClick={() => setShowPickModal(true)}
                        disabled={!canManage}
                        title={canManage ? undefined : t('imagingprocedures.no_permission', 'You do not have permission to change the scan menu (radiology.master.manage)')}
                        className={`group flex items-center justify-center gap-2 px-4 py-2 border-2 border-[#3AA8A8] text-[#3AA8A8] rounded-xl transition-all duration-300 shadow-sm font-semibold text-sm whitespace-nowrap ${!canManage ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-[#3AA8A8] hover:text-white hover:shadow-md'}`}
                    >
                        <Library className="w-5 h-5" />
                        {t('imagingprocedures.pick_from_master', 'Pick from master')}
                    </button>
                    <button
                        data-testid="add-procedure-button"
                        onClick={openCreate}
                        disabled={loading || !canManage}
                        title={canManage ? undefined : t('imagingprocedures.no_permission', 'You do not have permission to change the scan menu (radiology.master.manage)')}
                        className={`group flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg transition-all duration-300 font-semibold text-sm whitespace-nowrap ${loading || !canManage ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-xl hover:scale-105'}`}
                    >
                        <Plus className="w-5 h-5" />
                        {t('imagingprocedures.add', 'Add procedure')}
                    </button>
                </div>
            </div>

            <PickFromMasterModal
                kind="imaging_procedure"
                open={showPickModal}
                onClose={() => setShowPickModal(false)}
                onPicked={refetch}
            />

            <div className="mb-6 flex flex-col sm:flex-row gap-2 sm:items-center flex-wrap">
                <div className="relative flex bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 gap-2">
                    {['active', 'inactive'].map((tab) => (
                        <button
                            key={tab}
                            data-testid={`procedures-tab-${tab}`}
                            onClick={() => { setActiveTab(tab); setCurrentPage(1); }}
                            className={`relative px-6 py-2 text-sm rounded-xl transition-colors duration-300 flex-1 z-10 ${activeTab === tab ? 'text-white' : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'}`}
                        >
                            {activeTab === tab && (
                                <motion.div
                                    layoutId="activeProcedureStatusTab"
                                    className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span className="relative z-20">
                                {tab === 'active'
                                    ? t('imagingprocedures.on_menu', 'On the menu')
                                    : t('imagingprocedures.withdrawn_tab', 'Withdrawn')}
                            </span>
                        </button>
                    ))}
                </div>

                <div className="relative w-full sm:w-80">
                    <input
                        data-testid="procedures-search-input"
                        type="text"
                        placeholder={t('imagingprocedures.search_placeholder', 'Search name / code / body part…')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value.replace(/[<>]/g, '').slice(0, 60))}
                        maxLength={60}
                        className="w-full p-2 pl-10 pr-8 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                    />
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                    {searchQuery && (
                        <button
                            type="button"
                            onClick={() => setSearchQuery('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>

                <select
                    data-testid="procedures-modality-filter"
                    value={modalityFilter}
                    onChange={(e) => { setModalityFilter(e.target.value); setCurrentPage(1); }}
                    className="tt-select w-full sm:w-44 p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                >
                    <option value="">{t('imagingprocedures.all_modalities', 'All modalities')}</option>
                    {MODALITY_KEYS.map((m) => (
                        <option key={m} value={m}>{t(MODALITIES[m].labelKey, MODALITIES[m].label)}</option>
                    ))}
                </select>

                <select
                    data-testid="procedures-pndt-filter"
                    value={pndtFilter}
                    onChange={(e) => { setPndtFilter(e.target.value); setCurrentPage(1); }}
                    className="tt-select w-full sm:w-56 p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                >
                    <option value="">{t('imagingprocedures.all_procedures', 'Every procedure')}</option>
                    <option value="true">{t('imagingprocedures.only_pndt', 'PC-PNDT scope only')}</option>
                </select>
            </div>

            {/* The regulated half of the menu, counted where nobody has to go
                looking for it. The register an inspector asks to see is the list
                of procedures declared within the Act — if this number is zero on a
                department that runs obstetric ultrasound, the master is wrong and
                the scans are happening outside the Act. */}
            {!loading && pndtOnPage > 0 && (
                <div
                    data-testid="procedures-pndt-banner"
                    className="mb-4 flex items-start gap-3 p-3 rounded-xl bg-red-50 border-2 border-red-300"
                >
                    <ShieldAlert className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-800">
                        <span className="font-bold">
                            {t('imagingprocedures.pndt_count', '{{n}} procedure(s) on this page are within the PC-PNDT Act', { n: pndtOnPage })}
                        </span>
                        <p className="text-xs text-red-700 mt-0.5">
                            {t(
                                'imagingprocedures.pndt_banner_hint',
                                'On a female patient these cannot be recorded as performed until Form F is complete, they may only run on a PC-PNDT-registered machine, and the signed report carries the statutory declaration. Non-compliance is criminal, not a penalty.'
                            )}
                        </p>
                    </div>
                </div>
            )}

            {!loading && gapsOnPage > 0 && activeTab === 'active' && (
                <div data-testid="procedures-setup-gaps" className="mb-4 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    {t('imagingprocedures.setup_gaps', '{{n}} procedure(s) on this page are not finished being set up. A study with no preparation note sends the patient home to come back; one with no duration books a machine for the wrong length of time.', { n: gapsOnPage })}
                </div>
            )}

            {loading ? (
                <div className="flex flex-col items-center justify-center py-4 pt-12">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]">{t('imagingprocedures.loading', 'Loading...')}</span>
                </div>
            ) : (
                <>
                    <div className="hidden lg:block overflow-x-auto rounded-md border border-gray-200">
                        <table data-testid="procedures-table" className="w-full border-collapse rounded-md overflow-hidden">
                            <thead>
                                <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                    <th className="p-3 pl-2 text-left text-xs font-bold uppercase tracking-wider w-12 border-l-4 border-transparent">{t('imagingprocedures.serial_no', 'S.No')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('imagingprocedures.procedure', 'Procedure')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('imagingprocedures.modality', 'Modality')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('imagingprocedures.duration', 'Machine time')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('imagingprocedures.preparation', 'Preparation')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('imagingprocedures.price', 'Price')}</th>
                                    <th className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('imagingprocedures.action', 'Action')}</th>
                                </tr>
                            </thead>
                            <tbody data-testid="procedures-table-body">
                                {rows.length > 0 ? rows.map((p, index) => (
                                    <tr
                                        key={p.procedureId}
                                        data-testid={`procedure-row-${p.procedureId}`}
                                        // The rail and the tint are the second and third codings of
                                        // the same fact the badge states in words. A border rather
                                        // than a coloured <span>, because a background-coloured
                                        // element inside a <td> does not stretch to the row height.
                                        className={`border-b border-gray-100/50 transition-all duration-300 ${p.isPndt ? 'bg-red-50/40 hover:bg-red-50/70' : 'hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80'}`}
                                    >
                                        <td className={`p-3 pl-2 text-xs text-gray-600 border-l-4 ${p.isPndt ? 'border-red-600' : 'border-transparent'}`}>
                                            {indexOfFirst + index + 1}
                                        </td>
                                        <td className="p-3 text-xs"><Identity p={p} /></td>
                                        <td className="p-3 text-xs">
                                            <ModalityChip modality={p.modality} />
                                            {/* A disclosure, not a measurement — printed as
                                                stored so 'nil (non-ionising)' and '2–7 mSv' both
                                                survive. */}
                                            {p.radiationDose && (
                                                <div className="text-[10px] text-slate-500 mt-1 inline-flex items-center gap-1">
                                                    <Radiation className="w-3 h-3" />
                                                    {p.radiationDose}
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-3 text-xs text-gray-600 whitespace-nowrap">
                                            <span className="inline-flex items-center gap-1">
                                                <Clock className="w-3 h-3 text-[#4ABDBD]" />
                                                {formatDuration(p.durationMins)}
                                            </span>
                                        </td>
                                        <td className="p-3 text-xs text-gray-600 max-w-[280px]">
                                            {p.preparation
                                                ? <p className="line-clamp-3 whitespace-pre-line">{p.preparation}</p>
                                                : <span className="text-slate-300">—</span>}
                                            <div className="mt-1"><GapChips procedure={p} /></div>
                                        </td>
                                        <td className="p-3 text-xs text-gray-700 font-semibold whitespace-nowrap">
                                            ₹{money(p.price)}
                                        </td>
                                        <td className="p-3 text-xs"><Actions p={p} /></td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan={7} className="p-6 text-center text-gray-500">
                                            {activeTab === 'active'
                                                ? t('imagingprocedures.empty', 'Nothing on the menu yet. Add a procedure to give doctors something to order and the department something to book.')
                                                : t('imagingprocedures.empty_withdrawn', 'Nothing has been withdrawn.')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Cards below lg. Seven columns do not survive a narrow screen,
                        and the person reviewing a tariff is often on a tablet. */}
                    <div className="block lg:hidden space-y-4">
                        {rows.length > 0 ? rows.map((p) => (
                            <div
                                key={p.procedureId}
                                data-testid={`procedure-card-${p.procedureId}`}
                                className="flex rounded-xl shadow-md overflow-hidden bg-gray-50"
                            >
                                <span className={`w-1.5 flex-shrink-0 ${p.isPndt ? 'bg-red-600' : 'bg-[#4ABDBD]/40'}`} />
                                <div className="flex-1 p-4">
                                    <div className="flex justify-between items-start gap-2 mb-2">
                                        <Identity p={p} />
                                        <Actions p={p} compact />
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2 mt-3">
                                        <ModalityChip modality={p.modality} />
                                        <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                                            <Clock className="w-3 h-3 text-[#4ABDBD]" />
                                            {formatDuration(p.durationMins)}
                                        </span>
                                        <span className="text-xs font-semibold text-gray-700">₹{money(p.price)}</span>
                                    </div>
                                    {p.preparation && (
                                        <p className="text-[11px] text-slate-500 mt-2 whitespace-pre-line">{p.preparation}</p>
                                    )}
                                    <div className="mt-2"><GapChips procedure={p} /></div>
                                </div>
                            </div>
                        )) : (
                            <div className="p-6 text-center text-gray-500 text-sm">
                                {activeTab === 'active'
                                    ? t('imagingprocedures.empty', 'Nothing on the menu yet. Add a procedure to give doctors something to order and the department something to book.')
                                    : t('imagingprocedures.empty_withdrawn', 'Nothing has been withdrawn.')}
                            </div>
                        )}
                    </div>
                </>
            )}

            {!loading && total > 0 && (
                <Pagination
                    totalItems={total}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                />
            )}

            {/* ---- the form ---- */}
            {draft && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" data-testid="procedure-form-modal">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                            <h2 className="text-lg font-bold text-[#1a365d]">
                                {draft.procedureId
                                    ? t('imagingprocedures.edit_title', 'Edit procedure')
                                    : t('imagingprocedures.add', 'Add procedure')}
                            </h2>
                            <button type="button" onClick={() => setDraft(null)} data-testid="procedure-form-close" className="text-gray-400 hover:text-gray-600 cursor-pointer">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className={labelCls}>{t('imagingprocedures.code', 'Code')} *</label>
                                <input
                                    data-testid="procedure-form-code"
                                    value={draft.code}
                                    onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value.toUpperCase().slice(0, 30) }))}
                                    placeholder="XR-CHEST-PA"
                                    maxLength={30}
                                    className={`${inputCls} font-mono`}
                                />
                                {/* Unique inside this hospital by design. Two rows sharing
                                    a code means the order is priced and routed by whichever
                                    the query returned first — and if the two disagree on
                                    PC-PNDT, that coin toss decides whether a foetal scan
                                    happens inside the Act's register or outside it. */}
                                <p className="text-[11px] text-slate-400 mt-1">
                                    {t('imagingprocedures.code_hint', 'What staff type to order, and what your paper requisitions already say. Unique within this hospital.')}
                                </p>
                            </div>
                            <div>
                                <label className={labelCls}>{t('imagingprocedures.name', 'Reporting name')} *</label>
                                <input
                                    data-testid="procedure-form-name"
                                    value={draft.name}
                                    onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value.slice(0, 120) }))}
                                    placeholder={t('imagingprocedures.name_placeholder', 'e.g. MRI Brain with Contrast')}
                                    maxLength={120}
                                    className={inputCls}
                                />
                            </div>

                            <div>
                                <label className={labelCls}>{t('imagingprocedures.modality', 'Modality')} *</label>
                                <select
                                    data-testid="procedure-form-modality"
                                    value={draft.modality}
                                    onChange={(e) => setDraft((d) => ({ ...d, modality: e.target.value }))}
                                    className={`tt-select ${inputCls}`}
                                >
                                    {MODALITY_KEYS.map((m) => (
                                        <option key={m} value={m}>{t(MODALITIES[m].labelKey, MODALITIES[m].label)}</option>
                                    ))}
                                </select>
                                {/* The modality is the routing decision, not a label: it
                                    picks which machines can perform the study at all. */}
                                <p className="text-[11px] text-slate-400 mt-1">
                                    {t('imagingprocedures.modality_hint', 'Decides which machines can perform this study. A procedure whose modality no machine matches can never be booked.')}
                                </p>
                            </div>
                            <div>
                                <label className={labelCls}>{t('imagingprocedures.body_part', 'Body part')}</label>
                                <input
                                    data-testid="procedure-form-bodypart"
                                    value={draft.bodyPart}
                                    onChange={(e) => setDraft((d) => ({ ...d, bodyPart: e.target.value.slice(0, 60) }))}
                                    placeholder={t('imagingprocedures.body_part_placeholder', 'e.g. Chest, Right Knee, Whole Abdomen')}
                                    maxLength={60}
                                    className={inputCls}
                                />
                                <p className="text-[11px] text-slate-400 mt-1">
                                    {t('imagingprocedures.body_part_hint', 'What a radiologist filters a comparison search by — the prior film for the same part is the most useful thing to have open.')}
                                </p>
                            </div>

                            <div>
                                <label className={labelCls}>{t('imagingprocedures.price_label', 'Price (₹)')}</label>
                                <input
                                    data-testid="procedure-form-price"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={draft.price}
                                    onChange={(e) => setDraft((d) => ({ ...d, price: e.target.value }))}
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label className={labelCls}>{t('imagingprocedures.duration_label', 'Machine time (minutes)')} *</label>
                                <input
                                    data-testid="procedure-form-duration"
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={draft.durationMins}
                                    onChange={(e) => setDraft((d) => ({ ...d, durationMins: e.target.value }))}
                                    className={inputCls}
                                />
                                {/* Not the appointment length — the minutes the scanner is
                                    unavailable to anybody else. Left at a default an MRI
                                    brain stacks three patients into one scanner hour. */}
                                <p className="text-[11px] text-slate-400 mt-1">
                                    {t('imagingprocedures.duration_hint', 'How long the machine is blocked. A chest X-ray is five minutes; an MRI brain with contrast is forty-five.')}
                                </p>
                            </div>

                            <div className="sm:col-span-2 flex items-start gap-2 p-3 rounded-xl border border-violet-200 bg-violet-50/60">
                                <input
                                    id="procedure-contrast"
                                    data-testid="procedure-form-contrast"
                                    type="checkbox"
                                    checked={draft.requiresContrast}
                                    onChange={(e) => setDraft((d) => ({ ...d, requiresContrast: e.target.checked }))}
                                    className="w-4 h-4 mt-0.5 accent-[#4ABDBD]"
                                />
                                <label htmlFor="procedure-contrast" className="text-sm text-gray-700">
                                    {t('imagingprocedures.contrast_label', 'This study uses intravenous contrast')}
                                    <span className="block text-[11px] text-slate-500 mt-0.5">
                                        {t('imagingprocedures.contrast_hint', 'Brings in consent, an allergy history, a renal-function check and an IV line — which is why a contrast study can be refused at the console rather than discovered at the injector. Write the fasting instruction below.')}
                                    </span>
                                </label>
                            </div>

                            {/* ---- THE ONE THAT IS CRIMINAL, NOT A FINE ----
                                Given a whole block of its own, with the consequence
                                spelled out, because the cost of the two mistakes is
                                wildly asymmetric: ticked wrongly ON costs a form, left
                                wrongly OFF costs the department's registration and the
                                sonologist's licence. A bare checkbox in a row of
                                checkboxes does not carry that. */}
                            <div className={`sm:col-span-2 rounded-xl border-2 p-3 ${draft.isPndt ? 'border-red-400 bg-red-50' : 'border-red-200 bg-red-50/40'}`}>
                                <div className="flex items-start gap-2">
                                    <input
                                        id="procedure-pndt"
                                        data-testid="procedure-form-pndt"
                                        type="checkbox"
                                        checked={draft.isPndt}
                                        onChange={(e) => setDraft((d) => ({ ...d, isPndt: e.target.checked }))}
                                        className="w-4 h-4 mt-0.5 accent-red-600"
                                    />
                                    <label htmlFor="procedure-pndt" className="text-sm text-gray-800">
                                        <span className="font-bold text-red-800 inline-flex items-center gap-1.5">
                                            <ShieldAlert className="w-4 h-4" />
                                            {t('imagingprocedures.pndt_label', 'Within the PC-PNDT Act')}
                                        </span>
                                        <span className="block text-[11px] text-red-800/90 mt-1">
                                            {t(
                                                'imagingprocedures.pndt_form_hint',
                                                'Tick this for any scan capable of revealing foetal sex — obstetric, anomaly, NT, follicular study, and plain pelvic ultrasound on a woman of reproductive age. They share no common wording, which is why this is ticked and never guessed from the name.'
                                            )}
                                        </span>
                                    </label>
                                </div>

                                {draft.isPndt && (
                                    <ul data-testid="procedure-form-pndt-consequences" className="mt-3 pt-3 border-t border-red-200 text-[11px] text-red-900 space-y-1.5">
                                        <li className="flex items-start gap-1.5">
                                            <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                            {t('imagingprocedures.pndt_c1', 'On a female patient the study cannot be marked performed until Form F is complete — the department is refused with PNDT_FORM_REQUIRED.')}
                                        </li>
                                        <li className="flex items-start gap-1.5">
                                            <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                            {t('imagingprocedures.pndt_c2', 'It may only run on a machine whose PC-PNDT registration is current on the day of the scan.')}
                                        </li>
                                        <li className="flex items-start gap-1.5">
                                            <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                            {t('imagingprocedures.pndt_c3', 'The person conducting it must be a registered practitioner, and their registration number goes on the form.')}
                                        </li>
                                        <li className="flex items-start gap-1.5">
                                            <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                            {t('imagingprocedures.pndt_c4', 'The signed report carries the statutory line that the sex of the foetus has not been detected or disclosed, and the form is retained for two years from the scan.')}
                                        </li>
                                    </ul>
                                )}
                            </div>

                            <div className="sm:col-span-2">
                                <label className={labelCls}>{t('imagingprocedures.preparation_label', 'Patient preparation')}</label>
                                <textarea
                                    data-testid="procedure-form-preparation"
                                    value={draft.preparation}
                                    onChange={(e) => setDraft((d) => ({ ...d, preparation: e.target.value.slice(0, 1000) }))}
                                    rows={3}
                                    maxLength={1000}
                                    placeholder={t('imagingprocedures.preparation_placeholder', 'e.g. Full bladder, do not void. / 6 hours fasting. / Remove all metal; declare any implant — a pacemaker is an absolute contraindication.')}
                                    className={`${inputCls} resize-none`}
                                />
                                {/* Handed to the patient at ORDERING, not on arrival. An
                                    instruction that reaches them in the waiting room is an
                                    instruction they have already broken. */}
                                <p className="text-[11px] text-slate-400 mt-1">
                                    {t('imagingprocedures.preparation_hint', 'Printed on the order slip and shown at booking. Not given at the door — a patient who has eaten, or voided, loses the slot.')}
                                </p>
                            </div>

                            <div className="sm:col-span-2">
                                <label className={labelCls}>{t('imagingprocedures.dose_label', 'Typical radiation dose')}</label>
                                <input
                                    data-testid="procedure-form-dose"
                                    value={draft.radiationDose}
                                    onChange={(e) => setDraft((d) => ({ ...d, radiationDose: e.target.value.slice(0, 60) }))}
                                    placeholder={MODALITIES[draft.modality]?.ionising
                                        ? t('imagingprocedures.dose_placeholder_ionising', 'e.g. approx 0.1 mSv, or 2–7 mSv')
                                        : t('imagingprocedures.dose_placeholder_none', 'e.g. nil (non-ionising)')}
                                    maxLength={60}
                                    className={inputCls}
                                />
                                {/* A DISCLOSURE, in words, never a number: the figure is the
                                    typical exposure for the technique, while what a given
                                    patient received depends on their build and the factors
                                    the radiographer set. Stored as a number it invites
                                    summing a patient's yearly dose off the menu, which reads
                                    like a dose record and is not one. */}
                                <p className="text-[11px] text-slate-400 mt-1">
                                    {MODALITIES[draft.modality]?.ionising
                                        ? t('imagingprocedures.dose_hint_ionising', 'This modality emits ionising radiation. Quote the typical figure in words — it is a disclosure to the patient, not a dose record.')
                                        : t('imagingprocedures.dose_hint_none', 'Non-ionising. Leaving this blank is honest; a "0" printed beside a scan is a claim nobody checked.')}
                                </p>
                            </div>

                            {draft.procedureId && (
                                <p className="sm:col-span-2 text-[11px] text-slate-400">
                                    {t('imagingprocedures.edit_note', 'Editing the menu does not restate scans already performed. Every study and report keeps the procedure name it was recorded under.')}
                                </p>
                            )}
                        </div>

                        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
                            <button
                                type="button"
                                onClick={() => setDraft(null)}
                                className="px-4 py-2 text-sm rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-50 cursor-pointer"
                            >
                                {t('imagingprocedures.cancel', 'Cancel')}
                            </button>
                            <button
                                type="button"
                                onClick={save}
                                disabled={mutating}
                                data-testid="procedure-form-submit"
                                className="px-4 py-2 text-sm rounded-xl text-white font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] hover:shadow-lg disabled:opacity-60 flex items-center gap-2 cursor-pointer"
                            >
                                {mutating && <Loader2 className="w-4 h-4 animate-spin" />}
                                {draft.procedureId
                                    ? t('imagingprocedures.save', 'Save changes')
                                    : t('imagingprocedures.create', 'Add to the menu')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
